import { useMemo, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useIntl } from 'dibs-react-intl';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { hasKey } from 'dibs-ts-utils/exports/hasKey.ts';
import { trackSponsoredItemsEvent } from './trackSponsoredItemEvent.ts';
import { trackSponsoredLabelAbTestVariant } from './abTestHelpers.ts';

import { type useSponsoredItemsTracking_metadata$key } from './__generated__/useSponsoredItemsTracking_metadata.graphql.ts';

const metadataFragment = graphql`
    fragment useSponsoredItemsTracking_metadata on SponsoredItemsMetadata @relay(plural: true) {
        itemId
        impressionTrackerLink
        clickTrackerLink
    }
`;

export type MetadataMap = Record<
    string,
    { impressionTrackerLink: string | null; clickTrackerLink: string | null }
>;
type TrackerType = keyof MetadataMap[string];
type TrackerKeyMap = {
    [K in TrackerType as K extends `${infer Prefix}TrackerLink` ? Prefix : never]: K;
};

type HandleItemEvent = <T extends keyof TrackerKeyMap>(
    eventTrackerLink: T
) => (itemId: string | null | undefined) => void;

export const useSponsoredItemsTracking = ({
    metadata: metadataRef,
}: {
    metadata: useSponsoredItemsTracking_metadata$key | null | undefined;
}): HandleItemEvent => {
    const { locale } = useIntl();
    const metadata = useFragment(metadataFragment, metadataRef);

    const metadataMap = useMemo(
        () =>
            (metadata || []).filter(filterFalsy).reduce<MetadataMap>((obj, item) => {
                if (!item.itemId) {
                    return obj;
                }
                return {
                    ...obj,
                    [item.itemId]: {
                        impressionTrackerLink: item.impressionTrackerLink,
                        clickTrackerLink: item.clickTrackerLink,
                    },
                };
            }, {}),
        [metadata]
    );

    const handleItemEvent: HandleItemEvent = useCallback(
        <T extends keyof TrackerKeyMap>(eventTrackerLink: T) =>
            itemId => {
                const fullKey = `${eventTrackerLink}TrackerLink` as TrackerKeyMap[T];
                const link =
                    itemId && hasKey(metadataMap, itemId) ? metadataMap[itemId]?.[fullKey] : null;
                if (link) {
                    trackSponsoredItemsEvent(link);
                    if (eventTrackerLink === 'impression') {
                        trackSponsoredLabelAbTestVariant(locale);
                    }
                }
            },
        [metadataMap, locale]
    );

    return handleItemEvent;
};
